import { Component, computed, inject, input, model, output, signal, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { MaintenanceAsset } from '../../../shared/models/operation/maintenance-asset.model';
import { LccaTypeAutocompleteComponent } from '../../../shared/components/form-input/lcca-type-autocomplete/lcca-type-autocomplete.component';
import { LccaAutocompleteComponent } from '../../../shared/components/form-input/lcca-autocomplete/lcca-autocomplete.component';
import { Lcca } from '../../../shared/models/common/lcca.model';
import { AssetsService } from '../../../shared/services/assets.service';
import { Result } from '../../../shared/models/core/result.model';
import { environment } from '../../../../environments/environment';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

interface ParentAssetOption {
    id: number;
    asset_code?: string;
    grp_no?: string;
}

@Component({
    selector: 'app-maintenance-asset-form-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogModule,
        ButtonModule,
        InputTextModule,
        CalendarModule,
        InputNumberModule,
        TextareaModule,
        InputGroupModule,
        InputGroupAddonModule,
        AutoCompleteModule,
        LccaTypeAutocompleteComponent,
        LccaAutocompleteComponent
    ],
    templateUrl: './maintenance-asset-form-dialog.component.html',
    styleUrls: ['./maintenance-asset-form-dialog.component.scss']
})
export class MaintenanceAssetFormDialogComponent implements OnDestroy {
    private messageService = inject(MessageService);
    private assetsService = inject(AssetsService);
    private destroy$ = new Subject<void>();

    // Inputs
    record = input<MaintenanceAsset | null>(null);
    readOnly = input<boolean>(false);
    loading = input<boolean>(false);
    assetType = input<string>('pavements');
    requireParentAsset = input<boolean>(false);

    // Model for two-way visibility
    visible = model<boolean>(false);

    // Outputs
    save = output<Partial<MaintenanceAsset>>();
    cancel = output<void>();
    fileSelected = output<File>();

    // Form signals
    maintenanceDate = signal<Date | null>(null);
    nextMaintenanceDate = signal<Date | null>(null);
    maintenanceType = signal<string | number>('');
    maintenanceDetails = signal<number | null>(null);
    maintenanceDocumentation = signal('');
    documentationFile = signal<File | null>(null);
    quantity = signal<number | null>(null);
    rate = signal<number | null>(null);
    totalCost = signal<number | null>(null);
    parentAsset = signal<ParentAssetOption | null>(null);
    filteredParentAssets = signal<ParentAssetOption[]>([]);

    // Status
    isEditMode = computed(() => !!this.record()?.id);
    showParentAssetPicker = computed(() => this.requireParentAsset() && !this.isEditMode() && !this.readOnly());
    formSubmitted = signal(false);

    // Computed for displaying file name
    displayFileName = computed(() => {
        const file = this.documentationFile();
        if (file) return file.name;
        const doc = this.maintenanceDocumentation();
        if (doc) {
            // Extract filename from path
            return doc.split('/').pop() || doc;
        }
        return '';
    });

    // Validation
    maintenanceTypeError = computed(() => {
        if (!this.formSubmitted()) return '';
        const value = this.maintenanceType();
        const isValid = typeof value === 'number' ? value > 0 : value.trim() !== '';
        if (!isValid) return 'Maintenance Type is required';
        return '';
    });

    parentAssetError = computed(() => {
        if (!this.formSubmitted() || !this.showParentAssetPicker()) return '';
        if (!this.parentAsset()?.id) return 'Parent asset is required';
        return '';
    });

    isFormValid = computed(() => {
        return !this.maintenanceTypeError() && !this.parentAssetError();
    });

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    constructor() {
        // Sync form with record input
        effect(() => {
            const rec = this.record();
            if (rec) {
                this.maintenanceDate.set(rec.maintenance_date ? new Date(rec.maintenance_date) : null);
                this.nextMaintenanceDate.set(rec.next_maintenance_date ? new Date(rec.next_maintenance_date) : null);
                this.maintenanceType.set(rec.maintenance_type || '');
                this.maintenanceDetails.set(rec.maintenance_details ?? null);
                this.maintenanceDocumentation.set(rec.maintenance_documentation || '');
                this.quantity.set(rec.quantity ?? null);
                this.rate.set(rec.rate ?? null);
                this.totalCost.set(rec.total_cost ?? null);
                const masterRecId = rec.master_rec_id ?? rec.MASTER_REC_ID;
                if (masterRecId) {
                    this.parentAsset.set({
                        id: masterRecId,
                        asset_code: rec.asset_code,
                        grp_no: rec.grp_no
                    });
                } else {
                    this.parentAsset.set(null);
                }
            } else {
                this.resetForm();
            }
            this.formSubmitted.set(false);
        });

        // Auto-calculate Total Cost
        effect(() => {
            const qty = this.quantity();
            const rate = this.rate();

            // Only update if both values exist and we are not in read-only mode
            // Note: This allows manual override of total cost, but will reset it if qty/rate changes
            if (!this.readOnly() && qty !== null && rate !== null) {
                this.totalCost.set(qty * rate);
            }
        });
    }

    searchParentAssets(event: { query: string }): void {
        const query = (event.query || '').trim();
        this.assetsService.findAssetsByType<ParentAssetOption>(this.assetType(), 1, 25, { asset_code: query })
            .pipe(
                takeUntil(this.destroy$),
                catchError(() => of<Result<ParentAssetOption>>({ data: [], total: 0, page: 1, size: 25 }))
            )
            .subscribe(result => {
                this.filteredParentAssets.set(
                    (result.data || [])
                        .filter(asset => !!asset?.id)
                        .map(asset => ({
                            id: asset.id,
                            asset_code: asset.asset_code,
                            grp_no: asset.grp_no
                        }))
                );
            });
    }

    onParentAssetSelect(event: { value: ParentAssetOption }): void {
        this.parentAsset.set(event.value || null);
    }

    onParentAssetModelChange(value: ParentAssetOption | string | null): void {
        if (value && typeof value === 'object' && value.id) {
            this.parentAsset.set(value);
        } else if (!value) {
            this.parentAsset.set(null);
        }
    }

    onParentAssetClear(): void {
        this.parentAsset.set(null);
    }

    // Handle LCCA selection - auto-fill type and rate from LCCA data
    onLccaSelected(lcca: Lcca | null): void {
        if (lcca) {
            // Auto-fill maintenance type from LCCA's maintenanc_type
            if (lcca.maintenanc_type) {
                this.maintenanceType.set(lcca.maintenanc_type);
            }
            // Auto-fill rate from LCCA's rate
            if (lcca.rate !== undefined && lcca.rate !== null) {
                this.rate.set(lcca.rate);
            }
        }
    }

    // Handle file selection for documentation
    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            this.documentationFile.set(file);
            this.fileSelected.emit(file);
        }
    }

    // Remove selected file
    removeFile(): void {
        this.documentationFile.set(null);
        this.maintenanceDocumentation.set('');
        // Reset file input
        const fileInput = document.getElementById('documentation-file-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    // Get file URL for download/view
    getFileUrl(filePath: string | null | undefined): string {
        if (!filePath) return '';
        if (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.startsWith('data:')) {
            return filePath;
        }
        return `${environment.mediaHubUrl}/${filePath}`;
    }

    onSave(): void {
        this.formSubmitted.set(true);

        if (this.readOnly()) return;

        if (!this.isFormValid()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fix the errors in the form'
            });
            return;
        }

        const maintenanceTypeValue = this.maintenanceType();
        const selectedParent = this.parentAsset();
        const data: Partial<MaintenanceAsset> = {
            maintenance_date: this.maintenanceDate() || undefined,
            next_maintenance_date: this.nextMaintenanceDate() || undefined,
            maintenance_type: typeof maintenanceTypeValue === 'number' ? String(maintenanceTypeValue) : maintenanceTypeValue.trim(),
            maintenance_details: this.maintenanceDetails() ?? undefined,
            maintenance_documentation: this.maintenanceDocumentation().trim(),
            quantity: this.quantity() ?? undefined,
            rate: this.rate() ?? undefined,
            total_cost: this.totalCost() ?? undefined
        };

        if (selectedParent?.id) {
            data.master_rec_id = selectedParent.id;
            data.asset_code = selectedParent.asset_code;
            data.grp_no = selectedParent.grp_no;
        }

        this.save.emit(data);
    }

    onCancel(): void {
        this.visible.set(false);
        this.cancel.emit();
        this.resetForm();
    }

    private resetForm(): void {
        this.maintenanceDate.set(null);
        this.nextMaintenanceDate.set(null);
        this.maintenanceType.set('');
        this.maintenanceDetails.set(null);
        this.maintenanceDocumentation.set('');
        this.documentationFile.set(null);
        this.quantity.set(null);
        this.rate.set(null);
        this.totalCost.set(null);
        this.parentAsset.set(null);
        this.filteredParentAssets.set([]);
        this.formSubmitted.set(false);
    }
}
